import { Pressable, View, useWindowDimensions } from 'react-native';
import type { LayoutChangeEvent } from 'react-native';
import { Icon } from '@ant-design/react-native';
import { Button } from '@md-design-system/Button';
import { Head } from '@shared/components/head';
import { Text } from '@shared/components/text';
import { colorPalette } from '@shared/config/colorsConfig';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { TOUR_TOOLTIP_MAX_WIDTH, TOUR_VIEWPORT_PADDING } from '../constants';
import type { TTourStep } from '../types';
import { setTourTooltipHeight } from '../utils/viewport';

type TTourTooltipProps = {
  step: TTourStep;
  stepIndex: number;
  stepCount: number;
  isLast: boolean;
  onNext: () => void;
  onClose: () => void;
};

export const TourTooltip = ({
  step,
  stepIndex,
  stepCount,
  isLast,
  onNext,
  onClose,
}: TTourTooltipProps) => {
  const { t } = dynamicTranslations();
  const { width } = useWindowDimensions();

  const maxWidth = Math.min(TOUR_TOOLTIP_MAX_WIDTH, width - TOUR_VIEWPORT_PADDING * 2);

  const onLayout = (event: LayoutChangeEvent) =>
    setTourTooltipHeight(event.nativeEvent.layout.height);

  return (
    <View onLayout={onLayout} style={{ maxWidth }} className="bg-white rounded-[22px] px-6 py-5">
      <Pressable
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel={t('onboarding.close')}
        hitSlop={8}
        className="absolute right-3 top-3 w-10 h-10 rounded-full bg-white border border-gray-300 items-center justify-center z-10"
      >
        <Icon name="close" size={16} color={colorPalette.gray[500]} />
      </Pressable>

      {!!step.title && <Head type="h3" className="mb-2 pr-12">{step.title}</Head>}

      {!!step.content && <Text className="text-base text-gray-500">{step.content}</Text>}

      <View className="flex-row items-center justify-between mt-5">
        {stepCount > 1 ? (
          <Text className="text-md text-gray-500">
            {t('onboarding.stepCounter', { current: stepIndex + 1, total: stepCount })}
          </Text>
        ) : (
          <View />
        )}

        <Button variant="primary" size="medium" onPress={onNext}>
          {isLast ? t('onboarding.done') : t('onboarding.next')}
        </Button>
      </View>
    </View>
  );
};

export default TourTooltip;
